import { eUserType } from '@app/shared/enum/user-type';

/** The existing upgrade subscription page is the renewal flow for each role. */
export function getUpgradeSubscriptionRoute(userType: number): string | null {
  switch (userType) {
    case eUserType.SHIPPER:
    case eUserType.FINANCIAL_ADMIN:
      return '/shipper/upgrade-subscription';
    case eUserType.CARRIER:
    case eUserType.CARRIER_FINANCIAL_ADMIN:
      return '/carrier/upgrade-subscription';
    case eUserType.OWNER_OPERATOR:
      return '/owner-operator/upgrade-subscription';
    default:
      return null;
  }
}

/** The profile page, where the owner sees the subscription and the Auto-Renew setting, for each role that has a renewal page. */
export function getProfilePageRoute(userType: number): string | null {
  switch (userType) {
    case eUserType.SHIPPER:
    case eUserType.FINANCIAL_ADMIN:
      return '/shipper/profile';
    case eUserType.CARRIER:
    case eUserType.CARRIER_FINANCIAL_ADMIN:
      return '/carrier/profile';
    case eUserType.OWNER_OPERATOR:
      return '/owner-operator/profile';
    default:
      return null;
  }
}

/** The profile endpoint that carries the owner's subscription, for each role that has a renewal page. */
export function getSubscriptionProfileEndpoint(userType: number): string | null {
  switch (userType) {
    case eUserType.SHIPPER:
    case eUserType.FINANCIAL_ADMIN:
      return 'shipper/profile';
    case eUserType.CARRIER:
    case eUserType.CARRIER_FINANCIAL_ADMIN:
      return 'carrier/profile';
    case eUserType.OWNER_OPERATOR:
      return 'solodriver/profile';
    default:
      return null;
  }
}

/**
 * The only pages a signed-out visitor is returned to after signing in: the renewal pages (Renew Subscription links)
 * and the profile pages (Auto-Renew reminder links), and nothing else.
 */
const RENEWAL_ROUTES = [
  '/shipper/upgrade-subscription',
  '/carrier/upgrade-subscription',
  '/owner-operator/upgrade-subscription',
  '/shipper/profile',
  '/carrier/profile',
  '/owner-operator/profile'
];

/**
 * The return page an address points to, or null. Only an exact match with one of the pages above counts (a trailing
 * slash, query string and fragment are ignored), so an absolute address, a protocol-relative address or any other
 * page can never be used as a return destination.
 */
export function renewalPathOf(url: string | null | undefined): string | null {
  if (typeof url !== 'string') {
    return null;
  }
  const path = url.split(/[?#]/)[0].replace(/\/+$/, '');
  return RENEWAL_ROUTES.indexOf(path) !== -1 ? path : null;
}

/**
 * Where a user who just signed in should go when they came from a renewal or Auto-Renew link: the renewal page or the
 * profile page of their own role. Anything else (another role's page, an unknown role, a hostile value) returns null
 * and the normal landing page applies.
 */
export function resolveRenewalReturnUrl(requestedUrl: string | null | undefined, userType: number): string | null {
  const requested = renewalPathOf(requestedUrl);
  if (requested === null) {
    return null;
  }
  return requested === getUpgradeSubscriptionRoute(userType) || requested === getProfilePageRoute(userType)
    ? requested
    : null;
}
